/**
 * MailboxEditor — Iter Feb 2026 R47 · Phase 3.
 * Add/edit a mailbox (per-user personal OR workspace-shared).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Loader2 } from "lucide-react";
import { SmartRichTextDisplay } from "@/components/SmartRichText";

const empty = {
  name: "", scope: "personal",
  imap_host: "", imap_port: 993, imap_use_ssl: true,
  imap_username: "", imap_password: "",
  smtp_host: "", smtp_port: 587, smtp_use_tls: true,
  signature_html: "",
};

export default function MailboxEditor({ open, onOpenChange, mailboxId, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  // R69f · SMTP diagnostic wizard state
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpDiag, setSmtpDiag] = useState(null);
  const isEdit = !!mailboxId;

  useEffect(() => {
    if (!open) return;
    // R69f · Clear any prior diagnostic when dialog opens on a new mailbox
    setSmtpDiag(null);
    if (!isEdit) { setForm(empty); return; }
    api.get(`/workspaces/${activeWorkspaceId}/mailboxes`)
      .then(({ data }) => {
        const row = (data.rows || []).find(m => m.id === mailboxId);
        if (!row) return;
        setForm({
          ...empty,
          name: row.name,
          scope: row.owner_user_id ? "personal" : "workspace",
          imap_host: row.imap_host, imap_port: row.imap_port, imap_use_ssl: !!row.imap_use_ssl,
          smtp_host: row.smtp_host || "", smtp_port: row.smtp_port || 587, smtp_use_tls: !!row.smtp_use_tls,
          signature_html: row.signature_html || "",
          imap_username: "", imap_password: "",
        });
      });
  }, [open, mailboxId, activeWorkspaceId, isEdit]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give this mailbox a name"); return; }
    if (!form.imap_host.trim()) { toast.error("IMAP host required"); return; }
    if (!isEdit && !form.imap_username.trim()) { toast.error("Username required"); return; }
    if (!isEdit && !form.imap_password.trim()) { toast.error("Password required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, imap_port: parseInt(form.imap_port, 10) || 993, smtp_port: parseInt(form.smtp_port, 10) || 587 };
      if (isEdit) {
        await api.put(`/workspaces/${activeWorkspaceId}/mailboxes/${mailboxId}`, payload);
      } else {
        await api.post(`/workspaces/${activeWorkspaceId}/mailboxes`, payload);
      }
      toast.success("Mailbox saved (credentials encrypted)");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    if (!isEdit) { toast.info("Save first, then click Test"); return; }
    setTesting(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/mailboxes/${mailboxId}/test`);
      if (data.ok) toast.success(`OK — ${data.folders_count} folders found`);
      else toast.error(data.message || "Test failed");
    } catch (e) { toast.error(apiErrorMessage(e, "Test failed")); }
    finally { setTesting(false); }
  };

  // R69f · Diagnostic SMTP test — fires a self-addressed email and
  // reports per-phase timing (connect / STARTTLS / AUTH / DATA / QUIT).
  // Opens an inline results panel so admins can see exactly which phase
  // is slow.
  const runSmtpTest = async () => {
    if (!isEdit) { toast.info("Save first, then click Test send"); return; }
    setSmtpTesting(true);
    setSmtpDiag(null);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/mailboxes/${mailboxId}/test-send`);
      setSmtpDiag(data);
      if (data.ok) toast.success(`Diagnostic sent in ${data.total_ms}ms`);
      else toast.error(data.error || "Diagnostic failed — see details below");
    } catch (e) {
      const err = apiErrorMessage(e, "Test send failed");
      setSmtpDiag({ ok: false, error: err, phases: [], total_ms: 0 });
      toast.error(err);
    } finally { setSmtpTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="mailbox-editor">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit mailbox" : "Add mailbox"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-muted-foreground">Display name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Support (BigRock)" className="h-9 rounded-none" data-testid="mb-name" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Scope</Label>
              <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} className="h-9 w-full border border-border px-2 text-sm bg-background rounded-none" data-testid="mb-scope">
                <option value="personal">Personal (only me)</option>
                <option value="workspace">Workspace-shared (all members)</option>
              </select>
            </div>
          </div>

          <div className="border-t pt-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">IMAP (receive)</div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-[10px] text-muted-foreground">Host</Label>
                <Input value={form.imap_host} onChange={e => setForm({ ...form, imap_host: e.target.value })} placeholder="imap.hostgator.com" className="h-9 rounded-none font-mono text-xs" data-testid="mb-imap-host" />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Port</Label>
                <Input type="number" value={form.imap_port} onChange={e => setForm({ ...form, imap_port: e.target.value })} className="h-9 rounded-none font-mono text-xs" data-testid="mb-imap-port" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input value={form.imap_username} onChange={e => setForm({ ...form, imap_username: e.target.value })} placeholder={isEdit ? "•••• (leave blank to keep)" : "full email"} className="h-9 rounded-none font-mono text-xs" data-testid="mb-imap-username" />
              <Input type="password" value={form.imap_password} onChange={e => setForm({ ...form, imap_password: e.target.value })} placeholder={isEdit ? "•••• (leave blank to keep)" : "password"} className="h-9 rounded-none font-mono text-xs" data-testid="mb-imap-password" />
            </div>
            <label className="flex items-center gap-1 text-[11px] mt-1 cursor-pointer">
              <input type="checkbox" checked={form.imap_use_ssl} onChange={e => setForm({ ...form, imap_use_ssl: e.target.checked })} data-testid="mb-imap-ssl" />
              Use SSL (port 993)
            </label>
          </div>

          <div className="border-t pt-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">SMTP (send · optional — leave blank to use workspace default)</div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Input value={form.smtp_host} onChange={e => setForm({ ...form, smtp_host: e.target.value })} placeholder="smtp.hostgator.com" className="h-9 rounded-none font-mono text-xs" data-testid="mb-smtp-host" />
              </div>
              <Input type="number" value={form.smtp_port} onChange={e => setForm({ ...form, smtp_port: e.target.value })} className="h-9 rounded-none font-mono text-xs" data-testid="mb-smtp-port" />
            </div>
            <label className="flex items-center gap-1 text-[11px] mt-1 cursor-pointer">
              <input type="checkbox" checked={form.smtp_use_tls} onChange={e => setForm({ ...form, smtp_use_tls: e.target.checked })} data-testid="mb-smtp-tls" />
              STARTTLS (port 587)
            </label>
          </div>

          {/* R66 · Per-mailbox signature — overrides the user + workspace
              signature when composing through this mailbox. Perfect for
              running Sales / Support / Ops as distinct sender profiles.
              Accepts inline HTML so admins can paste rich sign-offs from
              Word / Gmail directly. */}
          <div className="border-t pt-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center justify-between">
              <span>Signature · overrides user + workspace default (optional)</span>
              {form.signature_html && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, signature_html: "" })}
                  className="text-[10px] text-rose-600 hover:underline"
                  data-testid="mb-signature-clear"
                >
                  Clear
                </button>
              )}
            </div>
            <textarea
              value={form.signature_html}
              onChange={e => setForm({ ...form, signature_html: e.target.value })}
              placeholder="<p>Best regards,<br/>Sales Team &middot; Aquarius</p>"
              rows={4}
              className="w-full border border-border bg-background px-2 py-1 text-xs font-mono rounded-none"
              data-testid="mb-signature"
            />
            {form.signature_html && (
              <div className="border border-dashed border-border p-2 mt-1 bg-neutral-50 dark:bg-neutral-900/40 text-[11px] text-muted-foreground"
                   data-testid="mb-signature-preview">
                <div className="uppercase text-[9px] tracking-widest mb-1">Preview</div>
                <SmartRichTextDisplay html={form.signature_html} />
              </div>
            )}
          </div>

          {/* R69f · SMTP diagnostic results panel */}
          {smtpDiag && (
            <div
              className={`border p-3 space-y-2 ${smtpDiag.ok ? "border-emerald-400/50 bg-emerald-50 dark:bg-emerald-950/20" : "border-rose-400/50 bg-rose-50 dark:bg-rose-950/20"}`}
              data-testid="mb-smtp-diag"
            >
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold uppercase tracking-widest">
                  SMTP diagnostic {smtpDiag.ok ? "· OK" : "· FAILED"}
                </span>
                <span className="text-muted-foreground font-mono">
                  {smtpDiag.total_ms}ms total · {smtpDiag.host}:{smtpDiag.port}
                </span>
              </div>
              <div className="grid grid-cols-5 gap-1 text-[10px]">
                {["connect", "starttls", "auth", "data", "quit"].map((name) => {
                  const p = (smtpDiag.phases || []).find((x) => x.name === name);
                  if (!p) return (
                    <div key={name} className="border border-border/40 bg-muted/30 px-2 py-1 text-center text-muted-foreground">
                      <div className="uppercase tracking-widest">{name}</div>
                      <div>—</div>
                    </div>
                  );
                  const slow = p.elapsed_ms > 5000;
                  return (
                    <div
                      key={name}
                      className={`border px-2 py-1 text-center ${p.ok ? (slow ? "border-amber-400 bg-amber-50 dark:bg-amber-950/20" : "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20") : "border-rose-400 bg-rose-50 dark:bg-rose-950/20"}`}
                      data-testid={`mb-smtp-phase-${name}`}
                      title={p.error || ""}
                    >
                      <div className="uppercase tracking-widest">{name}</div>
                      <div className="font-mono font-bold">{p.elapsed_ms}ms</div>
                      {!p.ok && <div className="text-[9px] truncate text-rose-700">{p.error}</div>}
                    </div>
                  );
                })}
              </div>
              {smtpDiag.advice && (
                <div className="text-[11px] italic text-foreground/80" data-testid="mb-smtp-advice">
                  💡 {smtpDiag.advice}
                </div>
              )}
              {smtpDiag.ok && smtpDiag.recipient && (
                <div className="text-[10px] text-muted-foreground">
                  Diagnostic email was sent to <span className="font-mono">{smtpDiag.recipient}</span>. Check your inbox to confirm delivery.
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={runTest} disabled={testing || !isEdit} className="rounded-none" data-testid="mb-test-imap">
            {testing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing…</> : "Test IMAP"}
          </Button>
          <Button variant="outline" onClick={runSmtpTest} disabled={smtpTesting || !isEdit} className="rounded-none border-primary/50 text-primary" data-testid="mb-test-smtp">
            {smtpTesting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Diagnosing…</> : "Test send (SMTP)"}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} className="rounded-none">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="mb-save">
            {busy ? "Saving…" : <><Save className="w-3 h-3 mr-1" /> Save</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
